import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, Injector, NgModule, SecurityContext } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { FullCalendarModule } from '@fullcalendar/angular';
import { Loader } from '@googlemaps/js-api-loader';
import { GraphQLModule, PlusAuthenticationService } from '@karve.it/core';
import { NavigationActionTiming, provideRouterStore, routerReducer } from '@ngrx/router-store';
import { StoreModule, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from "@ngrx/store-devtools";
import { ApolloModule } from 'apollo-angular';
import { MARKED_OPTIONS, MarkdownModule } from 'ngx-markdown';
import { NgxPrintModule } from 'ngx-print';
import { QuillModule } from 'ngx-quill';
import { ConfirmationService } from 'primeng/api';
import { CalendarModule } from 'primeng/calendar';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MenuModule } from 'primeng/menu';

import { environment } from 'src/environments/environment';

import { initCmd } from '../cmd';

import { AccountPageComponent } from './account-page/account-page.component';
import { McpConnectionsComponent } from './account-page/mcp-connections/mcp-connections.component';
import { TwoFactorSetupComponent } from './account-page/two-factor-setup/two-factor-setup.component';
import { ActOnComponent } from './act-on/act-on.component';
import { ContactListsComponent } from './act-on/contact-lists/contact-lists.component';
import { AppRoutingModule } from './app-routing.module';
import { AppCodeModule } from './app.code.component';
import { AppComponent } from './app.component';
import { AppMainComponent } from './app.main.component';
import { ArtifactDetailsComponent } from './artifacts/artifact-details/artifact-details.component';
import { ArtifactsComponent } from './artifacts/artifacts.component';
import { AssetDetailsComponent } from './assets/asset-details/asset-details.component';
import { AssetsComponent } from './assets/assets.component';
import { AuthenticationModule } from './authentication/authentication.module';
import { AvailabilityDetailsComponent } from './availability/availability-details/availability-details.component';
import { RemoveAvailabilityComponent } from './availability/remove-availability/remove-availability.component';
import { FeedbackDialogComponent } from './base/feedback-dialog/feedback-dialog.component';
import { AppFooterComponent } from './base/footer/app.footer.component';
import { AppMenuComponent } from './base/menu/app.menu.component';
import { MenuService } from './base/menu/app.menu.service';
import { AppMenuitemComponent } from './base/menu/app.menuitem.component';
import { QuickAccessComponent } from './base/menu/quick-access/quick-access.component';
import { MutateContainerComponent } from './base/mutate-container/mutate-container.component';
import { AppRightpanelComponent } from './base/rightpanel/app.rightpanel.component';
import { SearchComponent } from './base/search/search.component';
import { AppTopBarComponent } from './base/topbar/app.topbar.component';
import { BlueSageSettingsComponent } from './bluesage/bluesage-settings.component';
import { BusinessUnitsComponent } from './business-units/business-units.component';
import { FranchiseDetailsComponent } from './business-units/franchise-details/franchise-details.component';
import { ChargeDetailsComponent } from './charges/charge-details/charge-details.component';
import { ChargesComponent } from './charges/charges.component';
import { CommentsComponent } from './comments/comments.component';
import { CustomInputsModule } from './custom-inputs/custom-inputs.module';
import { DashboardModule } from './dashboard/dashboard.module';
import { CountryService } from './demo/service/countryservice';
import { CustomerService } from './demo/service/customerservice';
import { EventService } from './demo/service/eventservice';
import { IconService } from './demo/service/iconservice';
import { NodeService } from './demo/service/nodeservice';
import { PhotoService } from './demo/service/photoservice';
import { ProductService } from './demo/service/productservice';
import { ButtonDemoComponent } from './demo/view/buttondemo.component';
import { ChartsDemoComponent } from './demo/view/chartsdemo.component';
import { DashboardDemoComponent } from './demo/view/dashboarddemo.component';
import { DocumentationComponent } from './demo/view/documentation.component';
import { EmptyDemoComponent } from './demo/view/emptydemo.component';
import { FileDemoComponent } from './demo/view/filedemo.component';
import { FloatLabelDemoComponent } from './demo/view/floatlabeldemo.component';
import { FormLayoutDemoComponent } from './demo/view/formlayoutdemo.component';
import { InputDemoComponent } from './demo/view/inputdemo.component';
import { InvalidStateDemoComponent } from './demo/view/invalidstatedemo.component';
import { ListDemoComponent } from './demo/view/listdemo.component';
import { MediaDemoComponent } from './demo/view/mediademo.component';
import { MenusDemoComponent } from './demo/view/menusdemo.component';
import { MessagesDemoComponent } from './demo/view/messagesdemo.component';
import { MiscDemoComponent } from './demo/view/miscdemo.component';
import { OverlaysDemoComponent } from './demo/view/overlaysdemo.component';
import { PanelsDemoComponent } from './demo/view/panelsdemo.component';
import { TableDemoComponent } from './demo/view/tabledemo.component';
import { TreeDemoComponent } from './demo/view/treedemo.component';
import { DiscountDetailsComponent } from './discounts/discount-details/discount-details.component';
import { DiscountsComponent } from './discounts/discounts.component';
import { PreviewDynamicReportComponent } from './dynamic-reports/preview-dynamic-report/preview-dynamic-report.component';
import { FranchiseModule } from './franchise/franchise.module';
import { FreyaCommonModule } from './freya-common/freya-common.module';
import { HistoryDetailsComponent } from './history/history-details/history-details.component';
import { HistoryComponent } from './history/history.component';
import { HistorySearchComponent } from './history-search/history-search.component';
import { InvoiceDetailsComponent } from './invoices/invoice-details/invoice-details.component';
import { InvoicesComponent } from './invoices/invoices.component';
import { registerQuillModules } from './job/job-activity/quill-modules/quill-modules';
import { LeaveJobGuard } from './job/leave-workorders.guard';
import { JobDetailsComponent } from './jobs/job-details/job-details.component';
import { JobLocationsComponent } from './jobs/job-locations/job-locations.component';
import { JobParticipantComponent } from './jobs/job-participant/job-participant.component';
import { JobTabComponent } from './jobs/job-tab.component';
import { JobsComponent } from './jobs/jobs.component';
import { LibModule } from './lib.ts/lib.module';
import { LoadingComponent } from './loading/loading.component';
import { LoadingSkeletonComponent } from './loading-skeleton/loading-skeleton.component';
import { LocationDetailsComponent } from './locations/location-details/location-details.component';
import { LocationsComponent } from './locations/locations.component';
import { HowHeardOptionsComponent } from './marketing/how-heard-options/how-heard-options.component';
import { LeadSourceDetailsComponent } from './marketing/lead-source-details/lead-source-details.component';
import { LeadSourceSettingsComponent } from './marketing/lead-source-settings/lead-source-settings.component';
import { MarketingComponent } from './marketing/marketing.component';
import { CorporateComponent } from './mockups/corporate/corporate.component';
import { FranchisePartnersComponent } from './mockups/franchise-partners/franchise-partners.component';
import { LeadsComponent } from './mockups/leads/leads.component';
import { SettingsComponent } from './mockups/settings/settings.component';
import { NeedsAttentionComponent } from './needs-attention/needs-attention.component';
import { NiceJobsComponent } from './nice-jobs/nice-jobs.component';
import { NotificationViewerComponent } from './notifications/notification-viewer/notification-viewer.component';
import { NotificationsComponent } from './notifications/notifications.component';
import { AppAccessdeniedComponent } from './pages/app.accessdenied.component';
import { AppCalendarComponent } from './pages/app.calendar.component';
import { AppCrudComponent } from './pages/app.crud.component';
import { AppErrorComponent } from './pages/app.error.component';
import { AppHelpComponent } from './pages/app.help.component';
import { AppInvoiceComponent } from './pages/app.invoice.component';
import { AppNotfoundComponent } from './pages/app.notfound.component';
import { AppTimelineDemoComponent } from './pages/app.timelinedemo.component';
import { NoAvailableZonesComponent } from './pages/no-available-zones/no-available-zones.component';
import { OfflineComponent } from './pages/offline/offline.component';
import { PaymentRedirectComponent } from './payment/payment-redirect/payment-redirect.component';
import { PaymentComponent } from './payment/payment.component';
import { PaymentTypesComponent } from './payment-types/payment-types.component';
import { PriceDetailsComponent } from './products/price-details/price-details.component';
import { ProductDetailsComponent } from './products/product-details/product-details.component';
import { ProductsComponent } from './products/products.component';
import { QuickbooksAccountsComponent } from './quickbooks/quickbooks-accounts/quickbooks-accounts.component';
import { QuickbooksDashboardComponent } from './quickbooks/quickbooks-dashboard/quickbooks-dashboard.component';
import { QuickbooksLoginComponent } from './quickbooks/quickbooks-login/quickbooks-login.component';
import { QuickbooksComponent } from './quickbooks/quickbooks.component';
import { ReportsModule } from './reports/reports.module';
import { RingcentralComponent } from './ringcentral/ringcentral.component';
import { BookOffDetailsComponent } from './schedules/book-off-details/book-off-details.component';
import { CalendarEventDetailsComponent } from './schedules/calendar-event-details/calendar-event-details.component';
import { DailySummaryComponent } from './schedules/daily-summary/daily-summary.component';
import { DispatchCalendarRemarksDialogComponent } from './schedules/dispatch/components/dispatch-calendar-remarks-dialog/dispatch-calendar-remarks-dialog.component';
import { SchedulesComponent } from './schedules/schedules.component';
import { SearchResultsComponent } from './search-results/search-results.component';

import { LoaderWrapper } from './services/loader-wrapper.service';
import { CommunicationsComponent } from './settings/communications/communications.component';
import { CancelAndCloseDialogComponent } from './shared/cancel-and-close-dialog/cancel-and-close-dialog.component';
import { CreateDocumentDialogComponent } from './shared/create-document-dialog/create-document-dialog.component';
import { HighlightDatesDirective } from './shared/directives/highlight-dates.directive';
import { JobStatusPillComponent } from './shared/job-status-pill/job-status-pill.component';
import { RuleDetailsComponent } from './shared/rule-details/rule-details.component';
import { ScriptDetailsComponent } from './shared/script-details/script-details.component';
import { ScriptExecutionHistoryComponent } from './shared/script-execution-history/script-execution-history.component';
import { SearchJobResultComponent } from './shared/search-job-result/search-job-result.component';
import { SharedModule } from './shared/shared.module';
import { TemplateTypeDirective } from './shared/template-type.directive';
import { SortDirectionPipe } from './sort-direction.pipe';
import { rootState } from './state/rootState';
import { trackUnsavedChanges } from './state/unsaved-changes.reducer';
import { ApplyTransactionComponent } from './transactions/apply-transaction/apply-transaction.component';
import { CancelPayLinkDialogComponent } from './transactions/cancel-pay-link-dialog/cancel-pay-link-dialog.component';
import { CancelPreauthDialogComponent } from './transactions/cancel-preauth-dialog/cancel-preauth-dialog.component';
import { CreateTransactionDialogComponent } from './transactions/create-transaction-dialog/create-transaction-dialog.component';
import { TransactionDetailsComponent } from './transactions/transaction-details/transaction-details.component';
import { TransactionsComponent } from './transactions/transactions.component';
import { RoleDetailsComponent } from './users/role-details/role-details.component';
import { UserPageSimilarCustomersComponent } from './users/user-page/user-page-similar-customers/user-page-similar-customers.component';
import { UserPageComponent } from './users/user-page/user-page.component';
import { UsersDetailsComponent } from './users/users-details/users-details.component';
import { UsersComponent } from './users/users.component';
import { DisplayComponent } from './utilities/display.component';
import { ElevationComponent } from './utilities/elevation.component';
import { FlexboxComponent } from './utilities/flexbox.component';
import { GridComponent } from './utilities/grid.component';
import { IconsComponent } from './utilities/icons.component';
import { markedOptionsFactory } from './utilities/markdown';
import { SpacingComponent } from './utilities/spacing.component';
import { TextComponent } from './utilities/text.component';
import { TypographyComponent } from './utilities/typography.component';
import { WidgetsComponent } from './utilities/widgets.component';
import { YelpComponent } from './yelp/yelp.component';


registerQuillModules();

@NgModule({
    imports: [
        BrowserModule,
        FormsModule,
        AppRoutingModule,
        HttpClientModule,
        BrowserAnimationsModule,
        FreyaCommonModule,
        AppCodeModule,
        SharedModule,
        ReactiveFormsModule,
        // Standalone — imported so PaymentTypesComponent's template can use it.
        BlueSageSettingsComponent,
        LibModule,
        CalendarModule,
        MenuModule,
        FullCalendarModule,
        DashboardModule,
        ReportsModule,
        CustomInputsModule,
        JobStatusPillComponent,

        NgxPrintModule,

        MarkdownModule.forRoot({
            markedOptions: {
                provide: MARKED_OPTIONS,
                useFactory: markedOptionsFactory,
            },
            // Disable so that Markdown component may render heading IDs
            // Safe as long as all markdown content comes from us
            sanitize: SecurityContext.NONE,
        }),

        ApolloModule,
        GraphQLModule.forRoot({
            graphql: environment.apiUrl,
            ws: environment.websocket,
            websocketsEnabled: environment.webSocketsEnabled,
        }),
        QuillModule.forRoot({
            // modules: {},
        }),
        // Component Modules
        AuthenticationModule,
        FranchiseModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: environment.production,
            // Register the ServiceWorker as soon as the application is stable
            // or after 30 seconds (whichever comes first).
            registrationStrategy: 'registerImmediately'
        }),
        StoreModule.forRoot({}, {}),

        // STANDALONE COMPONENTS
        CreateTransactionDialogComponent,
        CreateDocumentDialogComponent,
        CancelPayLinkDialogComponent,
        CancelPreauthDialogComponent,
        CancelAndCloseDialogComponent,
        LeadSourceSettingsComponent,
        QuickAccessComponent,
        LoadingSkeletonComponent,
        HighlightDatesDirective,
        DailySummaryComponent,
        UserPageSimilarCustomersComponent,
        UsersComponent,
        DispatchCalendarRemarksDialogComponent,
    ],
    declarations: [
        AppComponent,
        AppMainComponent,
        AppRightpanelComponent,
        AppMenuComponent,
        AppMenuitemComponent,
        AppTopBarComponent,
        AppFooterComponent,
        DashboardDemoComponent,
        FormLayoutDemoComponent,
        FloatLabelDemoComponent,
        InvalidStateDemoComponent,
        InvoicesComponent,
        InputDemoComponent,
        ButtonDemoComponent,
        TableDemoComponent,
        ListDemoComponent,
        TreeDemoComponent,
        PanelsDemoComponent,
        OverlaysDemoComponent,
        MediaDemoComponent,
        MenusDemoComponent,
        MessagesDemoComponent,
        MessagesDemoComponent,
        MiscDemoComponent,
        ChartsDemoComponent,
        EmptyDemoComponent,
        FileDemoComponent,
        DocumentationComponent,
        DisplayComponent,
        ElevationComponent,
        FlexboxComponent,
        GridComponent,
        UsersDetailsComponent,
        IconsComponent,
        WidgetsComponent,
        SpacingComponent,
        TypographyComponent,
        TextComponent,
        AppCrudComponent,
        AppCalendarComponent,
        AppInvoiceComponent,
        AppHelpComponent,
        AppNotfoundComponent,
        AppErrorComponent,
        AppTimelineDemoComponent,
        AppAccessdeniedComponent,
        FranchisePartnersComponent,
        LeadsComponent,
        SchedulesComponent,
        SettingsComponent,
        AssetsComponent,
        AssetDetailsComponent,
        LocationDetailsComponent,
        ProductsComponent,
        CommunicationsComponent,
        ProductDetailsComponent,
        BusinessUnitsComponent,
        CorporateComponent,
        AvailabilityDetailsComponent,
        CalendarEventDetailsComponent,
        JobsComponent,
        JobTabComponent,
        JobDetailsComponent,
        FranchiseDetailsComponent,
        UserPageComponent,
        AccountPageComponent,
        TwoFactorSetupComponent,
        McpConnectionsComponent,
        LocationsComponent,
        TransactionsComponent,
        TransactionDetailsComponent,
        ArtifactsComponent,
        ArtifactDetailsComponent,
        CommentsComponent,
        PaymentComponent,
        HistorySearchComponent,
        PaymentRedirectComponent,
        MarketingComponent,
        DiscountsComponent,
        DiscountDetailsComponent,
        RoleDetailsComponent,
        RuleDetailsComponent,
        ScriptDetailsComponent,
        ScriptExecutionHistoryComponent,
        MutateContainerComponent,
        ChargesComponent,
        FeedbackDialogComponent,
        HistoryComponent,
        SortDirectionPipe,
        TemplateTypeDirective,
        NoAvailableZonesComponent,
        RemoveAvailabilityComponent,
        PriceDetailsComponent,
        ChargeDetailsComponent,
        OfflineComponent,
        LoadingComponent,
        JobLocationsComponent,
        NotificationsComponent,
        NotificationViewerComponent,
        HowHeardOptionsComponent,
        NiceJobsComponent,
        PaymentTypesComponent,
        NeedsAttentionComponent,
        ActOnComponent,
        ContactListsComponent,
        InvoiceDetailsComponent,
        ApplyTransactionComponent,
        BookOffDetailsComponent,
        QuickbooksComponent,
        QuickbooksLoginComponent,
        QuickbooksDashboardComponent,
        QuickbooksAccountsComponent,
        SearchComponent,
        SearchResultsComponent,
        SearchJobResultComponent,
        JobParticipantComponent,
        PreviewDynamicReportComponent,
        HistoryDetailsComponent,
        LeadSourceDetailsComponent,
        YelpComponent,
        RingcentralComponent,
    ],
    providers: [
        {
            // Bootstrap auth from httpOnly cookies before the app boots.
            // This calls authenticate({}) which uses the refresh-token cookie
            // to hydrate localStorage. The UserAuthGuard awaits this promise
            // before deciding to redirect to login.
            provide: APP_INITIALIZER,
            useFactory: (auth: PlusAuthenticationService) => () => auth.bootstrapAuth(),
            deps: [PlusAuthenticationService],
            multi: true,
        },
        {
            provide: LocationStrategy,
            useClass: PathLocationStrategy,
        },
        ConfirmationService,
        LeaveJobGuard,
        {
            provide: Loader,
            useFactory: (wrapper: LoaderWrapper) => wrapper.loader,
            deps: [LoaderWrapper],
        },
        CountryService,
        CustomerService,
        EventService,
        IconService,
        NodeService,
        PhotoService,
        ProductService,
        MenuService,
        AppMainComponent,
        DialogService,
        DynamicDialogRef,
        DynamicDialogConfig,
        provideStore({ router: routerReducer }, { metaReducers: [ trackUnsavedChanges ]}),
        provideRouterStore({ navigationActionTiming: NavigationActionTiming.PostActivation }),
        provideStoreDevtools({
            maxAge: 25, // Retains last 25 states
            // logOnly: environment.production, // Restrict extension to log-only mode
            autoPause: true, // Pauses recording actions and state changes when the extension window is not open
            // If set to true, will include stack trace for every dispatched action,
            // so you can see it in trace tab jumping directly to that part of code
            trace: true, 
            traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
            connectInZone: true // If set to true, the connection is established within the Angular zone
        }),
        ...rootState,
    ],
    bootstrap: [AppComponent]
})
export class AppModule {

    constructor(
        // used in cmd
        private injector: Injector,
    ) {
        initCmd(this.injector);
    }
}
